import React from "react";
import { connect } from "react-redux";

import {
    Alignment,
    FlexBehaviour,
    FlexDirection,
    Side,
    Size,
} from "../core/types/system/Layout";
import Address from "../core/types/core/Address";
import AddressEditor from "../core/components/addresses/AddressEditor";
import DropDown, {
    DropDownOption,
} from "../core/components/inputform/DropDown";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import Panel from "../core/components/panels/Panel";
import School from "../core/types/core/School";
import Textbox from "../core/components/inputform/Textbox";
import Timezone from "../core/types/core/Timezone";
import { IAppState } from "../core/state/Store";
import { initialiseGeographyDetails } from "../core/state/core/Geography";
import {
    initialiseSchoolDetails,
    reinitialiseSchoolDetails,
    updateSchool,
    updateSchoolAddress,
} from "../core/state/core/School";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import Button from "../core/components/buttons/Button";
import ButtonThemes from "../core/types/enums/ButtonThemes";
import Spacer from "../core/components/containers/Spacer";
import PanelHeader from "../core/components/panels/PanelHeader";
import {
    addCampus,
    initialiseCampusDetails,
    updateCampus,
} from "../core/state/core/Campus";
import Campus from "../core/types/core/Campus";

import "./ManageSchool.scss";
import IconStyles from "../core/types/enums/IconStyles";
import ContainerHeader from "../core/components/containers/ContainerHeader";
import TermDates from "../core/components/terms/TermDates";
import AcademicTermRange from "../core/types/core/AcademicTermRange";
import Dialog from "../core/components/dialog/Dialog";
import Info from "../core/components/info/Info";
import DatePicker from "../core/components/inputform/DatePicker";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogOK from "../core/components/dialog/DialogOK";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import AcademicTerm from "../core/types/core/AcademicTerm";
import RadioList from "../core/components/inputform/RadioList";
import InputFormOption from "../core/types/system/InputFormOption";
import RenderOrientation from "../core/types/enums/RenderOrientation";
import {
    addOrgUnit,
    initialiseOrgUnitDetails,
    updateOrgUnit,
} from "../core/state/core/OrgUnits";
import {
    OrganisationUnit,
    OrganisationUnitType,
} from "../core/types/core/OrganisationUnit";
import OrgUnit from "../core/components/structure/OrgUnit";
import Datastore from "../core/communication/Datastore";
import {
    ImportLog,
    SkippedRecordCount,
    UpsertCount,
} from "../core/types/core/ImportLog";
import { AppContext } from "../core/types/system/AppContext";
import Wizard from "../core/components/wizard/Wizard";
import WizardStep from "../core/components/wizard/WizardStep";
import FileExplorer from "../core/components/fileExplorer/FileExplorer";
import { FileExplorerTypes } from "../core/types/system/FileExplorer";
import File from "../core/types/files/File";
import Checkbox from "../core/components/inputform/Checkbox";
import Grid from "../core/components/grid/Grid";
import GridColumn from "../core/components/grid/GridColumn";

interface SchoolSetupOptions {
    archiveExisting: boolean;
}

interface RouteManageSchoolProps {
    school: School;
    timezone: Timezone;
    address: Address;
    timezones: Timezone[];
    campuses: Campus[];
    orgUnits: OrganisationUnit[];

    setPageLoading: (value: boolean) => void;
    initialiseGeographyDetails: () => Promise<void>;
    initialiseSchoolDetails: () => Promise<void>;
    initialiseCampusDetails: () => Promise<void>;
    initialiseOrgUnitDetails: () => Promise<void>;
    reinitialiseSchoolDetails: () => Promise<void>;
    updateSchool: (school: School) => Promise<void>;
    updateSchoolAddress: (address: Address) => Promise<void>;
    addCampus: (campuses: Campus[]) => Promise<void>;
    updateCampus: (campuses: Campus[]) => Promise<void>;
    addOrgUnit: (orgUnits: OrganisationUnit[]) => Promise<void>;
    updateOrgUnit: (orgUnits: OrganisationUnit[]) => Promise<void>;
}

interface RouteManageSchoolState {
    name: string;
    address: Address;
    timezoneId: string;
    campuses: Campus[];
    orgUnits: OrganisationUnit[];

    setupStep: number;
    setupOpen: boolean;
    setupWidth: number;
    setupFile: File;
    setupProceed: boolean;
    setupOptions: SchoolSetupOptions;
    setupResults: ImportLog;

    termsAddOpen: boolean;
    termsAddCampus: Campus;
    termsAddYear: number;
    termsAddMinYear: number;
    termsAddMaxYear: number;
    termsAddMonth: number;
    termsAddMonths: DropDownOption[];
    termsAddType: number;
    termsAddTypeOptions: InputFormOption[];
    rangeEditOpen: boolean;
    rangeEditValue: AcademicTermRange;
}

class RouteManageSchool extends React.Component<
    RouteManageSchoolProps,
    RouteManageSchoolState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: RouteManageSchoolState = {
        name: "",
        address: null,
        timezoneId: "",
        campuses: [],
        orgUnits: [],

        setupStep: 0,
        setupOpen: false,
        setupWidth: 550,
        setupFile: null,
        setupProceed: false,
        setupOptions: { archiveExisting: false },
        setupResults: null,

        termsAddOpen: false,
        termsAddCampus: null,
        termsAddYear: 0,
        termsAddMinYear: 0,
        termsAddMaxYear: 0,
        termsAddMonth: 0,
        termsAddMonths: new Array(12).fill(0).map((_, index) => ({
            text: new Date(0, index).toLocaleString("default", {
                month: "long",
            }),
            value: index,
            order: index,
        })),
        termsAddType: 0,
        termsAddTypeOptions: [
            { text: "Terms", value: 0 },
            { text: "Trimesters", value: 1 },
        ],
        rangeEditOpen: false,
        rangeEditValue: null,
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await Promise.all([
            this.props.initialiseGeographyDetails(),
            this.props.initialiseSchoolDetails(),
            this.props.initialiseCampusDetails(),
            this.props.initialiseOrgUnitDetails(),
        ]);
        this.props.setPageLoading(false);

        this.setState({
            name: this.props.school?.name ?? "",
            address: { ...this.props.address },
            timezoneId: this.props.timezone?.id ?? "",
            campuses: this.props.campuses.map(c => new Campus(c)),
            orgUnits: this.props.orgUnits,
        });
    }

    componentDidUpdate(
        prevProps: Readonly<RouteManageSchoolProps>,
        prevState: Readonly<RouteManageSchoolState>,
        snapshot?: any,
    ): void {
        const name = this.props.school?.name ?? "";
        if (
            (!this.state.name && !!name) ||
            prevProps.school !== this.props.school
        ) {
            this.setState({
                name: name,
            });
        }
        const address = this.props.address;
        if (
            (!this.state.address && !!address) ||
            prevProps.address !== this.props.address
        ) {
            this.setState({
                address: { ...address },
            });
        }
        const timezone = this.props.timezone;
        if (
            (!this.state.timezoneId && !!timezone) ||
            prevProps.timezone !== this.props.timezone
        ) {
            this.setState({
                timezoneId: timezone?.id ?? "",
            });
        }
        const campuses = this.props.campuses;
        if (prevProps.campuses !== this.props.campuses) {
            this.setState({
                campuses: campuses.map(c => new Campus(c)),
            });
        }
        const orgUnits = this.props.orgUnits;
        if (prevProps.orgUnits !== this.props.orgUnits) {
            this.setState({
                orgUnits: orgUnits,
            });
        }
    }

    setup() {
        this.setState({
            setupOpen: true,
            setupStep: 0,
            setupWidth: 550,
            setupFile: null,
            setupProceed: false,
            setupOptions: { archiveExisting: false },
        });
    }

    setupCancel() {
        this.setState({
            setupOpen: false,
        });
    }

    setupBack() {
        const step = this.state.setupStep - 1;
        this.setState({
            setupStep: step,
        });

        if (step === 0) {
            this.setState({
                setupFile: null,
                setupProceed: false,
            });
        }
    }

    async setupNext() {
        const step = this.state.setupStep + 1;
        this.setState({
            setupStep: step,
        });

        if (step === 2) await this.setupComplete();
    }

    setupCanNext() {
        if (this.state.setupStep === 0) return !!this.state.setupFile;
        if (this.state.setupStep === 1) return true;
        return false;
    }

    async setupComplete() {
        try {
            if (await this.context.verifyCredentials()) {
                const result = await Datastore.postType<ImportLog>(
                    `api/Schools/Setup/TDF/${this.state.setupFile.id}`,
                    this.state.setupOptions,
                );

                // await Promise.all([
                //     this.yearLevelsLinkedDataService.release(),
                //     this.facultiesLinkedDataService.release(),
                //     this.studentsLinkedDataService.release(),
                //     this.teachersLinkedDataService.release(),
                //     this.subjectsLinkedDataService.release(),
                //     this.roomsLinkedDataService.release(),
                //     this.areasLinkedDataService.release(),
                // ]);
                await this.props.reinitialiseSchoolDetails();

                this.setState({
                    setupStep: this.state.setupStep + 1,
                    setupResults: result,
                });
            }
        } catch {
            this.setState({
                setupStep: this.state.setupStep - 1,
            });
        }
    }

    resetSchoolDetails() {
        this.setState({
            name: this.props.school?.name ?? "",
            address: { ...this.props.address },
            timezoneId: this.props.timezone?.id ?? "",
        });
    }

    schoolDirty() {
        return (
            this.state.name !== (this.props.school?.name ?? "") ||
            JSON.stringify(this.state.address) !==
                JSON.stringify(this.props.address) ||
            this.state.timezoneId !== (this.props.timezone?.id ?? "")
        );
    }

    async saveSchoolDetails() {
        try {
            if (await this.context.verifyCredentials()) {
                this.props.setPageLoading(true);

                await Promise.all([
                    this.props.updateSchool({
                        ...this.props.school,
                        name: this.state.name,
                        timezone: this.state.timezoneId,
                    }),
                    this.props.updateSchoolAddress({
                        ...this.props.address,
                        ...this.state.address,
                    }),
                ]);
            }
        } finally {
            this.props.setPageLoading(false);
        }
    }

    addCampus() {
        this.setState({
            campuses: this.state.campuses.concat(
                new Campus({
                    id: 0,
                    name: `Campus ${this.state.campuses.length + 1}`,
                    address: new Address(),
                    terms: [],
                }),
            ),
        });
    }

    async removeCampus(campus: Campus) {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "warning",
            styleColor: "yellow",
            title: "Confirm Campus Removal",
            text: `Are you sure you want to remove campus <b>${campus.name}</b>?`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response)
            this.setState({
                campuses: this.state.campuses.filter(c => c.id !== campus.id),
            });
    }

    addTermDates(campus: Campus) {
        this.setState({
            termsAddOpen: true,
            termsAddCampus: campus,
            termsAddYear: new Date().getFullYear(),
            termsAddMinYear: new Date().getFullYear() - 7,
            termsAddMaxYear: new Date().getFullYear() + 5,
            termsAddMonth: 0,
            termsAddType: 0,
        });
    }

    addTermDatesSave() {
        if (
            this.state.termsAddCampus.terms.some(
                item => item.year === this.state.termsAddYear,
            )
        ) {
            addAlert({
                icon: "error",
                styleColor: "red",
                title: "Term Dates Already Exist",
                text: "The selected campus already has a term date definition for the selected year.",
            });
            return;
        }

        const newTermDate = new AcademicTerm({
            year: this.state.termsAddYear,
            ranges: [],
        });

        let startDate = new Date(
            this.state.termsAddYear,
            this.state.termsAddMonth,
            1,
        );

        switch (this.state.termsAddType) {
            case 0:
                newTermDate.ranges.push(
                    new AcademicTermRange({
                        name: "Semester 1",
                        startDate: startDate,
                        endDate: startDate.addMonths(6).addDays(-1),
                        children: [],
                    }),
                );
                newTermDate.ranges.push(
                    new AcademicTermRange({
                        name: "Semester 2",
                        startDate: startDate.addMonths(6),
                        endDate: startDate.addMonths(12).addDays(-1),
                        children: [],
                    }),
                );

                let termIndex = 0;
                const termCount = 2; // Default to 2 terms per year for semester-based configuration
                for (const range of newTermDate.ranges) {
                    const totalDays = range.endDate.daysBetween(
                        range.startDate,
                    );
                    const segmentLength = Math.floor(totalDays / termCount);

                    let startDate = range.startDate;
                    for (let index = 0; index < termCount; index++) {
                        range.children.push(
                            new AcademicTermRange({
                                name: `Term ${++termIndex}`,
                                startDate: startDate,
                                endDate:
                                    index === termCount - 1
                                        ? range.endDate
                                        : startDate.addDays(segmentLength - 1),
                            }),
                        );

                        startDate = startDate.addDays(segmentLength);
                    }
                }
                break;
            case 1:
                newTermDate.ranges.push(
                    new AcademicTermRange({
                        name: "Trimester 1",
                        startDate: startDate,
                        endDate: startDate.addMonths(4).addDays(-1),
                        children: [],
                    }),
                );
                newTermDate.ranges.push(
                    new AcademicTermRange({
                        name: "Trimester 2",
                        startDate: startDate.addMonths(4),
                        endDate: startDate.addMonths(8).addDays(-1),
                        children: [],
                    }),
                );
                newTermDate.ranges.push(
                    new AcademicTermRange({
                        name: "Trimester 3",
                        startDate: startDate.addMonths(8),
                        endDate: startDate.addMonths(12).addDays(-1),
                        children: [],
                    }),
                );
                break;
        }

        const working = new Campus(this.state.termsAddCampus);
        working.terms.push(newTermDate);

        this.setState({
            campuses: this.state.campuses.map(c =>
                c.id === working.id ? working : c,
            ),
            termsAddOpen: false,
            termsAddCampus: null,
        });
    }

    async removeTermDates(campus: Campus, range: AcademicTerm) {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "warning",
            styleColor: "yellow",
            title: "Confirm Term Dates Removal",
            text: `Are you sure you want to remove the term dates for <b>${range.display}</b> from campus <b>${campus.name}</b>?`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            const working = new Campus(campus);
            working.removeTermsByRangeId(range.id);
            this.setState({
                campuses: this.state.campuses
                    .filter(c => c.id !== campus.id)
                    .concat(working),
            });
        }
    }

    editDateRange(range: AcademicTermRange) {
        this.setState({
            rangeEditOpen: true,
            rangeEditValue: new AcademicTermRange(range),
        });
    }

    editRangeSave() {
        const campus = this.state.campuses.find(c =>
            c.hasRangeId(this.state.rangeEditValue.id),
        );

        if (campus) {
            const working = new Campus(campus);
            working.replaceRange(this.state.rangeEditValue);

            this.setState({
                campuses: this.state.campuses.map(c =>
                    c.id === working.id ? working : c,
                ),
                rangeEditOpen: false,
                rangeEditValue: null,
            });
        }
    }

    resetCampusDetails() {
        this.setState({
            campuses: this.props.campuses.map(c => new Campus(c)),
        });
    }

    canSaveCampus() {
        return this.state.campuses.every(c => !c.hasError());
    }

    async saveCampusDetails() {
        if (this.canSaveCampus())
            try {
                this.props.setPageLoading(true);
                const addCampuses = this.state.campuses.filter(c => c.id === 0);
                const existingCampuses = this.state.campuses.filter(
                    c => c.id !== 0,
                );
                const originalCampuses = structuredClone(this.props.campuses);

                const waits = [];
                if (addCampuses.length > 0)
                    waits.push(this.props.addCampus(addCampuses));
                if (existingCampuses.length > 0)
                    waits.push(this.props.updateCampus(existingCampuses));
                await Promise.all(waits);

                if (!!existingCampuses.length) {
                    let shouldNotifyLater = false;
                    let shouldNotifyEarly = false;

                    for (const campus of existingCampuses) {
                        const original = originalCampuses.find(
                            item => item.id === campus.id,
                        );
                        if (!original) continue;

                        let newS = new Date(
                            campus.terms
                                .flatMap(item => item.ranges)
                                .map(item => item.startDate.toDate().valueOf())
                                .min(),
                        );
                        let oldS = new Date(
                            original.terms
                                .flatMap(item => item.ranges)
                                .map(item => item.startDate.toDate().valueOf())
                                .min(),
                        );

                        if (newS.compareDate(oldS) < 0)
                            shouldNotifyEarly = true;
                        else if (newS.compareDate(oldS) > 0)
                            shouldNotifyLater = true;
                    }

                    if (shouldNotifyEarly)
                        addAlert({
                            text:
                                "The start date for the Academic Year has been changed " +
                                "to an earlier date. The start date and day have been " +
                                "updated in the Daily Organiser to reflect this.<br/><br/>" +
                                "A timetable will need to be published for this new start " +
                                "date.<br/><br/>In the Daily Organiser please check that " +
                                "the data on [2A] Calendar - Timetable Days and [2C] " +
                                "Calendar - Cycle Start Dates is correct.",
                        });

                    if (shouldNotifyLater)
                        addAlert({
                            text:
                                "The start date for the Academic Year has been changed " +
                                "to a later date. The start date and day have been updated " +
                                "in the Daily Organiser to reflect this.<br/><br/>In the " +
                                "Daily Organiser please check that the data on [2A] " +
                                "Calendar - Timetable Days and [2C] Calendar - Cycle Start " +
                                "Dates is correct.",
                        });
                }

                const tempOrgs = structuredClone(
                    this.state.orgUnits ?? [],
                )[0] ?? {
                    id: 0,
                    type: OrganisationUnitType.School,
                    name: this.state.name,
                    children: [],
                    members: [],
                };
                for (const campus of [...addCampuses, ...existingCampuses]) {
                    const original = originalCampuses.find(
                        item => item.id === campus.id,
                    );
                    const unit = !!original
                        ? tempOrgs.children.find(
                              item =>
                                  item.type === OrganisationUnitType.Campus &&
                                  item.name === original.name,
                          )
                        : null;

                    if (unit) unit.name = campus.name;
                    else
                        tempOrgs.children.push({
                            id: 0,
                            type: OrganisationUnitType.Campus,
                            name: campus.name,
                            children: [],
                            members: [],
                        });
                }

                for (const unit of tempOrgs.children.slice()) {
                    if (unit.type !== OrganisationUnitType.Campus) continue;

                    if (
                        !this.state.campuses.some(
                            campus => campus.name === unit.name,
                        )
                    )
                        tempOrgs.children = tempOrgs.children.filter(
                            child => child.id !== unit.id,
                        );
                }

                if (tempOrgs.id === 0) await this.props.addOrgUnit([tempOrgs]);
                else await this.props.updateOrgUnit([tempOrgs]);
            } finally {
                this.props.setPageLoading(false);
            }
    }

    campusesDirty() {
        return (
            JSON.stringify(this.state.campuses) !==
            JSON.stringify(this.props.campuses)
        );
    }

    addOrgUnit(unit: OrganisationUnit) {
        const temp = structuredClone(this.state.orgUnits);
        const replacement = structuredClone(unit);
        replacement.children.push({
            id: 0,
            name: "New department",
            type: OrganisationUnitType.Department,
            children: [],
            members: [],
        });
        for (const orgUnit of temp)
            if (this.replaceChildOrgUnit(orgUnit, unit, replacement)) break;
        this.setState({
            orgUnits: temp,
        });
    }

    remOrgUnit(unit: OrganisationUnit) {
        const temp = structuredClone(this.state.orgUnits);
        for (const orgUnit of temp)
            if (this.replaceChildOrgUnit(orgUnit, unit)) break;
        this.setState({
            orgUnits: temp,
        });
    }

    typeOrgUnit(unit: OrganisationUnit, type: OrganisationUnitType) {
        const temp = structuredClone(this.state.orgUnits);
        const replacement = structuredClone(unit);
        replacement.type = type;
        for (const orgUnit of temp)
            if (this.replaceChildOrgUnit(orgUnit, unit, replacement)) break;
        this.setState({
            orgUnits: temp,
        });
    }

    nameOrgUnit(unit: OrganisationUnit, name: string) {
        const temp = structuredClone(this.state.orgUnits);
        const replacement = structuredClone(unit);
        replacement.name = name;
        for (const orgUnit of temp)
            if (this.replaceChildOrgUnit(orgUnit, unit, replacement)) break;
        this.setState({
            orgUnits: temp,
        });
    }

    replaceChildOrgUnit(
        parent: OrganisationUnit,
        unit: OrganisationUnit,
        replacement: OrganisationUnit = null,
    ): boolean {
        const index = parent.children.findIndex(c => c.id === unit.id);
        if (index !== -1) {
            if (replacement) {
                parent.children[index] = replacement;
            } else {
                parent.children.splice(index, 1);
            }
            return true;
        }

        for (const child of parent.children)
            if (this.replaceChildOrgUnit(child, unit, replacement)) return true;
        return false;
    }

    async saveStructure() {
        try {
            this.props.setPageLoading(true);
            await this.props.updateOrgUnit(this.state.orgUnits);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    resetStructure() {
        this.setState({
            orgUnits: this.props.orgUnits,
        });
    }

    structureDirty() {
        return (
            JSON.stringify(this.state.orgUnits) !==
            JSON.stringify(this.props.orgUnits)
        );
    }

    render() {
        return (
            <section>
                <GeneralContainer
                    scaling={true}
                    mid={true}
                    max={true}
                    bufferSide={Side.All}
                    clearanceSide={Side.Top}
                    clearanceSize={Size.Large}
                    alignment={Alignment.Center}>
                    <Panel>
                        <PanelHeader>
                            <h1>School Information</h1>
                        </PanelHeader>

                        <Panel
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Small}
                            flex={FlexDirection.Row}>
                            <div className="col-pane right-buffer-small">
                                <i className="blue section">location_city</i>
                            </div>
                            <div className="col">
                                <h2>Name</h2>

                                <div className="full top-clearance-small">
                                    <Textbox
                                        value={this.state.name}
                                        onChange={value =>
                                            this.setState({ name: value })
                                        }
                                    />
                                </div>
                            </div>
                        </Panel>

                        <Panel
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Small}
                            flex={FlexDirection.Row}>
                            <div className="col-pane right-buffer-small">
                                <i className="blue section">globe_asia</i>
                            </div>
                            <div className="col">
                                <h2>Time Zone</h2>

                                <div className="full top-clearance-small">
                                    <DropDown
                                        noType={true}
                                        fullWidth={true}
                                        value={this.state.timezoneId}
                                        options={this.props.timezones?.map(
                                            tz => ({
                                                text: tz.displayName,
                                                value: tz.id,
                                            }),
                                        )}
                                        onChange={value =>
                                            this.setState({
                                                timezoneId: value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </Panel>

                        <Panel
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Small}
                            flex={FlexDirection.Row}>
                            <div className="col-pane right-buffer-small">
                                <i className="blue section">location_on</i>
                            </div>
                            <div className="col">
                                <h2>Address</h2>

                                <div className="full top-clearance-small">
                                    <AddressEditor
                                        value={this.state.address}
                                        onChange={value =>
                                            this.setState({ address: value })
                                        }
                                    />
                                </div>
                            </div>
                        </Panel>

                        <GeneralContainer
                            flex={FlexDirection.Row}
                            flexSpaced={true}
                            clearanceSide={Side.Top}>
                            <Button
                                secure={true}
                                text="Setup Using Timetable"
                                onClick={this.setup.bind(this)}
                            />

                            <Spacer />

                            <Button
                                text="Reset"
                                disabled={!this.schoolDirty()}
                                onClick={this.resetSchoolDetails.bind(this)}
                            />
                            <Button
                                secure={true}
                                text="Save"
                                theme={ButtonThemes.Theme}
                                disabled={!this.schoolDirty()}
                                onClick={this.saveSchoolDetails.bind(this)}
                            />
                        </GeneralContainer>
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>Campus Information</h1>

                            <Spacer />

                            <Button
                                icon="add"
                                text="Add Campus"
                                tooltip="Add new campus"
                                tooltipPlace="bottom"
                                onClick={this.addCampus.bind(this)}
                            />
                        </PanelHeader>

                        {this.state.campuses?.map((campus, index) => (
                            <Panel
                                key={index}
                                clearanceSide={Side.Top}
                                clearanceSize={
                                    index === 0 ? Size.Small : Size.Medium
                                }
                                flex={FlexDirection.Column}>
                                <Button
                                    icon="close"
                                    iconStyle={IconStyles.Red}
                                    inline={true}
                                    align={Alignment.Right}
                                    section={true}
                                    tooltip="Remove this campus"
                                    tooltipPlace="right"
                                    onClick={() => this.removeCampus(campus)}
                                />

                                <GeneralContainer
                                    flex={FlexDirection.Row}
                                    fullWidth={true}>
                                    <GeneralContainer
                                        clearanceSide={Side.Right}
                                        clearanceSize={Size.Small}
                                        flexBehaviour={FlexBehaviour.Min}>
                                        <i className="blue section">domain</i>
                                    </GeneralContainer>

                                    <GeneralContainer
                                        flex={FlexDirection.Column}
                                        flexSpaced={true}
                                        flexBehaviour={FlexBehaviour.Max}>
                                        <h2>Name</h2>

                                        <Textbox
                                            value={campus.name}
                                            onChange={value => {
                                                const campuses =
                                                    this.state.campuses.map(c =>
                                                        c.id === campus.id
                                                            ? new Campus({
                                                                  ...c,
                                                                  name: value,
                                                              })
                                                            : c,
                                                    );
                                                this.setState({ campuses });
                                            }}
                                        />
                                    </GeneralContainer>
                                </GeneralContainer>

                                <GeneralContainer
                                    flex={FlexDirection.Row}
                                    ruleSide={Side.Top}
                                    clearanceSide={Side.Top}
                                    clearanceSize={Size.Small}
                                    bufferSide={Side.Top}
                                    bufferSize={Size.Small}
                                    fullWidth={true}>
                                    <GeneralContainer
                                        clearanceSide={Side.Right}
                                        clearanceSize={Size.Small}
                                        flexBehaviour={FlexBehaviour.Min}>
                                        <i className="red section">
                                            location_on
                                        </i>
                                    </GeneralContainer>

                                    <GeneralContainer
                                        flex={FlexDirection.Column}
                                        flexSpaced={true}
                                        flexBehaviour={FlexBehaviour.Max}>
                                        <h2>Address</h2>

                                        <AddressEditor
                                            value={campus.address}
                                            onChange={value => {
                                                const campuses =
                                                    this.state.campuses.map(c =>
                                                        c.id === campus.id
                                                            ? new Campus({
                                                                  ...c,
                                                                  address:
                                                                      value,
                                                              })
                                                            : c,
                                                    );
                                                this.setState({ campuses });
                                            }}
                                        />
                                    </GeneralContainer>
                                </GeneralContainer>

                                <GeneralContainer
                                    flex={FlexDirection.Row}
                                    ruleSide={Side.Top}
                                    clearanceSide={Side.Top}
                                    clearanceSize={Size.Small}
                                    bufferSide={Side.Top}
                                    bufferSize={Size.Small}
                                    fullWidth={true}>
                                    <GeneralContainer
                                        clearanceSide={Side.Right}
                                        clearanceSize={Size.Small}
                                        flexBehaviour={FlexBehaviour.Min}>
                                        <i className="blue section">
                                            date_range
                                        </i>
                                    </GeneralContainer>

                                    <GeneralContainer
                                        flex={FlexDirection.Column}
                                        flexSpaced={true}
                                        flexBehaviour={FlexBehaviour.Max}>
                                        <ContainerHeader>
                                            <h2>Term Dates</h2>

                                            <Button
                                                icon="add"
                                                inline={true}
                                                align={Alignment.Right}
                                                tooltip="Add new term dates definition"
                                                tooltipPlace="right"
                                                onClick={() =>
                                                    this.addTermDates(campus)
                                                }
                                            />
                                        </ContainerHeader>

                                        {campus.terms?.map((term, index) => (
                                            <TermDates
                                                key={index}
                                                value={term}
                                                onEditRange={this.editDateRange.bind(
                                                    this,
                                                )}
                                                onRemoveRange={() =>
                                                    this.removeTermDates(
                                                        campus,
                                                        term,
                                                    )
                                                }
                                            />
                                        ))}

                                        {campus.terms?.length === 0 && (
                                            <span>
                                                There are no term dates defined
                                                for this campus.
                                            </span>
                                        )}
                                    </GeneralContainer>
                                </GeneralContainer>
                            </Panel>
                        ))}

                        <GeneralContainer
                            flex={FlexDirection.ReverseRow}
                            flexSpaced={true}
                            clearanceSide={Side.Top}>
                            <Button
                                text="Save"
                                theme={ButtonThemes.Theme}
                                disabled={
                                    !this.campusesDirty() ||
                                    !this.canSaveCampus()
                                }
                                onClick={this.saveCampusDetails.bind(this)}
                            />
                            <Button
                                text="Reset"
                                disabled={!this.campusesDirty()}
                                onClick={this.resetCampusDetails.bind(this)}
                            />
                        </GeneralContainer>
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>School Structure</h1>
                        </PanelHeader>

                        <Panel>
                            <Info>
                                Here you can define the structure of your school
                                including campuses, faculties, year levels or
                                departments. This information can be used
                                throughout your Timetabling Solutions apps to
                                manage data and access to it.
                            </Info>
                        </Panel>

                        <Panel
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Small}>
                            {this.state.orgUnits.map(orgUnit => (
                                <OrgUnit
                                    key={orgUnit.id}
                                    value={orgUnit}
                                    onAdd={this.addOrgUnit.bind(this)}
                                    onRemove={this.remOrgUnit.bind(this)}
                                    onTypeChange={this.typeOrgUnit.bind(this)}
                                    onNameChange={this.nameOrgUnit.bind(this)}
                                />
                            ))}
                        </Panel>

                        <GeneralContainer
                            flex={FlexDirection.ReverseRow}
                            flexSpaced={true}
                            clearanceSide={Side.Top}>
                            <Button
                                text="Save"
                                theme={ButtonThemes.Theme}
                                disabled={!this.structureDirty()}
                                onClick={this.saveStructure.bind(this)}
                            />
                            <Button
                                text="Reset"
                                disabled={!this.structureDirty()}
                                onClick={this.resetStructure.bind(this)}
                            />
                        </GeneralContainer>
                    </Panel>
                </GeneralContainer>

                <Dialog
                    dialogIcon="date_range"
                    dialogTitle="Add Term Date Definition"
                    showDialog={this.state.termsAddOpen}
                    canResize={false}
                    width={700}>
                    <DialogContent>
                        <Panel>
                            <h2>Academic Year</h2>

                            <Panel clearanceSide={Side.Top}>
                                <Info>
                                    Which academic year should the new
                                    definition be for?
                                </Info>

                                <GeneralContainer flex={FlexDirection.Row}>
                                    <Textbox
                                        type="number"
                                        width="100px"
                                        value={String(this.state.termsAddYear)}
                                        min={this.state.termsAddMinYear}
                                        max={this.state.termsAddMaxYear}
                                        onChange={value =>
                                            this.setState({
                                                termsAddYear: Number(value),
                                            })
                                        }
                                    />

                                    <span className="col-pane title onesixth inline">
                                        Start Month:
                                    </span>

                                    <DropDown
                                        noType={true}
                                        value={this.state.termsAddMonth}
                                        options={this.state.termsAddMonths}
                                        onChange={value =>
                                            this.setState({
                                                termsAddMonth: value,
                                            })
                                        }
                                    />
                                </GeneralContainer>
                            </Panel>
                        </Panel>

                        <Panel clearanceSide={Side.Top}>
                            <h2>Term/Trimester Configuration</h2>

                            <Panel clearanceSide={Side.Top}>
                                <Info>
                                    Does your school use terms or trimesters?
                                </Info>

                                <GeneralContainer fullWidth={true}>
                                    <RadioList
                                        display={RenderOrientation.Horizontal}
                                        value={this.state.termsAddType}
                                        options={this.state.termsAddTypeOptions}
                                        onChange={value =>
                                            this.setState({
                                                termsAddType: value,
                                            })
                                        }
                                    />
                                </GeneralContainer>
                            </Panel>
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            onClick={() =>
                                this.setState({ termsAddOpen: false })
                            }
                        />
                        <DialogOK onClick={this.addTermDatesSave.bind(this)} />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    dialogIcon="date_range"
                    dialogTitle="Edit Term Dates Range"
                    showDialog={this.state.rangeEditOpen}
                    canResize={false}
                    width={700}>
                    <DialogContent>
                        <Panel>
                            <h2>{this.state.rangeEditValue?.name}</h2>

                            <Panel clearanceSide={Side.Top}>
                                <Info>
                                    Enter the start and end dates of&nbsp;
                                    {this.state.rangeEditValue?.name}.
                                </Info>

                                <Panel
                                    clearanceSide={Side.Top}
                                    clearanceSize={Size.Small}
                                    flex={FlexDirection.Row}>
                                    <span className="col-pane title onesixth inline">
                                        Start Date:
                                    </span>

                                    <DatePicker
                                        className="col"
                                        value={this.state.rangeEditValue?.startDate?.toDate()}
                                        onChange={value => {
                                            this.setState({
                                                rangeEditValue:
                                                    new AcademicTermRange({
                                                        ...this.state
                                                            .rangeEditValue,
                                                        startDate: value,
                                                    }),
                                            });
                                        }}
                                    />
                                </Panel>
                                <Panel
                                    clearanceSide={Side.Top}
                                    clearanceSize={Size.Small}
                                    flex={FlexDirection.Row}>
                                    <span className="col-pane title onesixth inline">
                                        End Date:
                                    </span>

                                    <DatePicker
                                        className="col"
                                        value={this.state.rangeEditValue?.endDate?.toDate()}
                                        onChange={value => {
                                            this.setState({
                                                rangeEditValue:
                                                    new AcademicTermRange({
                                                        ...this.state
                                                            .rangeEditValue,
                                                        endDate: value,
                                                    }),
                                            });
                                        }}
                                    />
                                </Panel>
                            </Panel>
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            onClick={() =>
                                this.setState({ rangeEditOpen: false })
                            }
                        />
                        <DialogOK onClick={this.editRangeSave.bind(this)} />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    dialogIcon="manufacturing"
                    dialogTitle="Setup School"
                    showDialog={this.state.setupOpen}
                    canResize={false}
                    width={this.state.setupWidth}>
                    <DialogContent>
                        <Wizard
                            flow={true}
                            frameless={true}
                            transparent={true}
                            clearanceSide={Side.Top}
                            step={this.state.setupStep}>
                            <WizardStep step={0}>
                                <Info>Select Setup Timetable File</Info>
                                <FileExplorer
                                    type={FileExplorerTypes.Timetables}
                                    value={this.state.setupFile}
                                    onChange={item =>
                                        this.setState({
                                            setupFile: item,
                                        })
                                    }
                                />
                            </WizardStep>
                            <WizardStep
                                step={1}
                                framed={true}>
                                <PanelHeader>
                                    <h1>Setup Options</h1>
                                </PanelHeader>

                                <Checkbox
                                    label="Archive Information Not In Timetable"
                                    value={
                                        this.state.setupOptions.archiveExisting
                                    }
                                    onChange={value =>
                                        this.setState({
                                            setupOptions: {
                                                ...this.state.setupOptions,
                                                archiveExisting: value,
                                            },
                                        })
                                    }
                                />
                            </WizardStep>
                            <WizardStep
                                step={2}
                                framed={true}
                                hiddenWhenOffscreen={true}>
                                <div className="buffer text-center">
                                    <h2>We're currently importing your data</h2>
                                </div>
                                <div className="process-indicator">
                                    <div className="cube cube1"></div>
                                    <div className="cube cube2"></div>
                                    <div className="cube cube3"></div>
                                    <div className="cube cube4"></div>
                                    <div className="cube cube5"></div>
                                    <div className="cube cube6"></div>
                                    <div className="cube cube7"></div>
                                    <div className="cube cube8"></div>
                                    <div className="cube cube9"></div>
                                    <div className="cube cube10"></div>
                                    <div className="cube cube11"></div>
                                    <div className="document1"></div>
                                    <div className="document2"></div>
                                </div>
                            </WizardStep>
                            <WizardStep
                                step={3}
                                hiddenWhenOffscreen={true}>
                                <Panel>
                                    <Panel title="Addition/Update Counts">
                                        {this.state?.setupResults?.upsertCounts
                                            ?.length > 0 ? (
                                            <Grid<UpsertCount>
                                                value={
                                                    this.state.setupResults
                                                        ?.upsertCounts ?? []
                                                }
                                                keyField="name"
                                                fullWidth={true}>
                                                <GridColumn
                                                    header="Type"
                                                    field="name"
                                                />
                                                <GridColumn
                                                    header="Added"
                                                    field="insertCount"
                                                />
                                                <GridColumn
                                                    header="Updated"
                                                    field="updateCount"
                                                />
                                            </Grid>
                                        ) : (
                                            <Info>
                                                No records were added or
                                                updated.
                                            </Info>
                                        )}
                                    </Panel>

                                    <Panel
                                        title="Skipped Items"
                                        clearanceSide={Side.Top}>
                                        {this.state?.setupResults?.skippedItems
                                            ?.length > 0 ? (
                                            <Grid<SkippedRecordCount>
                                                value={
                                                    this.state.setupResults
                                                        ?.skippedItems ?? []
                                                }
                                                keyField="name"
                                                fullWidth={true}>
                                                <GridColumn
                                                    header="Type"
                                                    field="name"
                                                />
                                                <GridColumn
                                                    header="Number"
                                                    field="count"
                                                />
                                                <GridColumn
                                                    header="Reason"
                                                    field="reason"
                                                />
                                            </Grid>
                                        ) : (
                                            <Info>
                                                No records were skipped.
                                            </Info>
                                        )}
                                    </Panel>
                                </Panel>
                            </WizardStep>
                        </Wizard>
                    </DialogContent>
                    <DialogFooter full={true}>
                        {this.state.setupStep > 0 && this.state.setupStep < 2 && (
                            <DialogCancel
                                text="Back"
                                onClick={this.setupBack.bind(this)}
                            />
                        )}
                        <Spacer />
                        {this.state.setupStep !== 2 && (
                            <DialogCancel
                                text={
                                    this.state.setupStep < 3
                                        ? "Cancel"
                                        : "Close"
                                }
                                onClick={this.setupCancel.bind(this)}
                            />
                        )}
                        {this.state.setupStep < 2 && (
                            <DialogOK
                                text="Next"
                                disabled={!this.setupCanNext()}
                                onClick={this.setupNext.bind(this)}
                            />
                        )}
                    </DialogFooter>
                </Dialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { school, geography, campus, orgUnits } = state.db;
    return {
        school: school.school,
        timezone: school.timezone,
        address: school.address,
        timezones: geography.timezones,
        campuses: campus.campuses,
        orgUnits: orgUnits.orgUnits,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        initialiseGeographyDetails: () =>
            unwrappedDispatch(initialiseGeographyDetails()),
        initialiseSchoolDetails: () =>
            unwrappedDispatch(initialiseSchoolDetails()),
        initialiseCampusDetails: () =>
            unwrappedDispatch(initialiseCampusDetails()),
        initialiseOrgUnitDetails: () =>
            unwrappedDispatch(initialiseOrgUnitDetails()),
        reinitialiseSchoolDetails: () =>
            unwrappedDispatch(reinitialiseSchoolDetails()),
        updateSchool: (school: School) =>
            unwrappedDispatch(updateSchool(school)),
        updateSchoolAddress: (address: Address) =>
            unwrappedDispatch(updateSchoolAddress(address)),
        addCampus: (campuses: Campus[]) =>
            unwrappedDispatch(addCampus(campuses)),
        updateCampus: (campuses: Campus[]) =>
            unwrappedDispatch(updateCampus(campuses)),
        addOrgUnit: (orgUnits: OrganisationUnit[]) =>
            unwrappedDispatch(addOrgUnit(orgUnits)),
        updateOrgUnit: (orgUnits: OrganisationUnit[]) =>
            unwrappedDispatch(updateOrgUnit(orgUnits)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteManageSchool);
